import { useQuery, useMutation } from '@tanstack/react-query';
import {
  reportService,
  RevenueReportFilters,
  TransactionReportFilters,
  CustomerReportFilters,
} from '../services';

// Revenue Reports
export const useRevenueReport = (filters: RevenueReportFilters, enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'revenue', filters],
    queryFn: async () => {
      const response = await reportService.getRevenueReport(filters);
      return response.data;
    },
    enabled,
  });

  return {
    report: data,
    isLoading,
    error,
  };
};

// Wallet Report
export const useWalletReport = (
  filters: { date_filter?: string; start_date?: string; end_date?: string },
  enabled = true
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'wallet', filters],
    queryFn: async () => {
      const response = await reportService.getWalletReport(filters);
      return response.data;
    },
    enabled,
  });

  return { report: data, isLoading, error };
};

export const useDownloadRevenueReport = () => {
  const downloadMutation = useMutation({
    mutationFn: async (filters: RevenueReportFilters) => {
      const blob = await reportService.downloadRevenueReport(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `revenue-report-${filters.start_date}-${filters.end_date}.${filters.format || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });

  return {
    downloadReport: downloadMutation.mutateAsync,
    isDownloading: downloadMutation.isPending,
  };
};

// Transaction Reports
export const useTransactionReport = (filters: TransactionReportFilters, enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'transactions', filters],
    queryFn: async () => {
      const response = await reportService.getTransactionReport(filters);
      return response.data;
    },
    enabled,
  });

  return {
    report: data,
    isLoading,
    error,
  };
};

export const useDownloadTransactionReport = () => {
  const downloadMutation = useMutation({
    mutationFn: async (filters: TransactionReportFilters) => {
      const blob = await reportService.downloadTransactionReport(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transaction-report-${filters.start_date}-${filters.end_date}.${filters.format || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });

  return {
    downloadReport: downloadMutation.mutateAsync,
    isDownloading: downloadMutation.isPending,
  };
};

// Customer Reports
export const useCustomerReport = (filters?: CustomerReportFilters, enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'customers', filters],
    queryFn: async () => {
      const response = await reportService.getCustomerReport(filters);
      return response.data;
    },
    enabled,
  });

  return {
    report: data,
    isLoading,
    error,
  };
};

export const useDownloadCustomerReport = () => {
  const downloadMutation = useMutation({
    mutationFn: async (filters?: CustomerReportFilters) => {
      const blob = await reportService.downloadCustomerReport(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer-report-${new Date().toISOString().split('T')[0]}.${filters?.format || 'pdf'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });

  return {
    downloadReport: downloadMutation.mutateAsync,
    isDownloading: downloadMutation.isPending,
  };
};

// Service Performance Report
export const useServicePerformanceReport = (startDate: string, endDate: string, enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'service-performance', startDate, endDate],
    queryFn: async () => {
      const response = await reportService.getServicePerformanceReport(startDate, endDate);
      return response.data;
    },
    enabled: enabled && !!startDate && !!endDate,
  });

  return {
    report: data,
    isLoading,
    error,
  };
};

// Collection Efficiency Report
export const useCollectionEfficiencyReport = (startDate: string, endDate: string, enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'collection-efficiency', startDate, endDate],
    queryFn: async () => {
      const response = await reportService.getCollectionEfficiencyReport(startDate, endDate);
      return response.data;
    },
    enabled: enabled && !!startDate && !!endDate,
  });

  return {
    report: data,
    isLoading,
    error,
  };
};

// Outstanding Balances Report
export const useOutstandingBalancesReport = (asOf?: string, enabled = true) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'outstanding-balances', asOf],
    queryFn: async () => {
      const response = await reportService.getOutstandingBalancesReport(asOf);
      return response.data;
    },
    enabled,
  });

  return {
    report: data,
    isLoading,
    error,
  };
};
